import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, Loader2, FileText, Send, Calendar, Trash2, Eye, CheckCircle2, XCircle,
  Clock, Link2, Copy, Search, Receipt, Wrench, ArrowRight, Pencil, FileSpreadsheet,
  MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import ExcelImportDialog from "@/components/ExcelImportDialog";
import VehiculeCard from "@/components/VehiculeCard";
import DevisForm from "@/components/devis/DevisForm";
import EmailComposeModal from "@/components/mail/EmailComposeModal";

function fmtChf(n) {
  return (n || 0).toLocaleString("fr-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(s) {
  if (!s) return "—";
  const [y, m, d] = s.slice(0, 10).split("-");
  return `${d}.${m}.${y}`;
}

const STATUT_CONFIG = {
  en_suspens: { label: "En suspens", color: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock },
  accepte:    { label: "Accepté",    color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  refuse:     { label: "Refusé",     color: "bg-red-50 text-red-700 border-red-200", icon: XCircle },
  planifie:   { label: "Planifié",   color: "bg-blue-50 text-blue-700 border-blue-200", icon: Calendar },
};

// Cycle métier UNIFIÉ — utilisé pour les tabs Facturation > Devis.
// Source : champ `cycle` calculé côté backend dans /devis-all (envoye/accepte/refuse/expire/converti).
const CYCLE_TABS = [
  { value: "all",      label: "Tous",      color: "text-gray-700" },
  { value: "envoye",   label: "Envoyés",   color: "text-amber-700" },
  { value: "accepte",  label: "Acceptés",  color: "text-emerald-700" },
  { value: "refuse",   label: "Refusés",   color: "text-red-700" },
  { value: "expire",   label: "Expirés",   color: "text-orange-700" },
  { value: "converti", label: "Convertis", color: "text-blue-700" },
];

export default function DevisTab({ initialDevisId = null }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cycle, setCycle] = useState("all");  // tab cycle métier (envoye/accepte/refuse/expire/converti)
  const [stats, setStats] = useState({ all: 0, envoye: 0, accepte: 0, refuse: 0, expire: 0, converti: 0 });
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(initialDevisId ? { id: initialDevisId } : null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (cycle !== "all") params.cycle = cycle;
      if (search.trim()) params.q = search.trim();
      // Source unique : vue unifiée Devis Facturation + Devis Atelier (OR)
      const { data } = await api.get("/devis-all", { params });
      setItems(data.items || []);
    } finally { setLoading(false); }
  }, [cycle, search]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get("/devis-all/stats");
      setStats(data || {});
    } catch (_) { /* silencieux */ }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);
  useEffect(() => { fetchStats(); }, [fetchStats, items.length]);

  const handleRowClick = (d) => {
    if (d.source === "or") {
      // Devis Atelier → ouvre la fiche OR
      navigate(d.lien);
    } else {
      // Devis Facturation standalone → ouvre la dialog détail
      setDetail({ id: d.id });
    }
  };

  return (
    <div data-testid="devis-tab">
      {/* Tabs cycle métier — Envoyés / Acceptés / Refusés / Expirés / Convertis */}
      {/* iter254 — mobile : menu déroulant au lieu de la bande d'onglets */}
      <div className="sm:hidden mb-3">
        <Select value={cycle} onValueChange={setCycle}>
          <SelectTrigger className="w-full" data-testid="devis-cycle-menu">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CYCLE_TABS.map((t) => (
              <SelectItem key={t.value} value={t.value} data-testid={`devis-cycle-menu-${t.value}`}>
                {t.label} ({stats?.[t.value] ?? 0})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="hidden sm:flex items-center gap-1 mb-3 border-b border-gray-200 overflow-x-auto">
        {CYCLE_TABS.map((t) => {
          const active = cycle === t.value;
          const count = stats?.[t.value] ?? 0;
          return (
            <button
              key={t.value}
              onClick={() => setCycle(t.value)}
              data-testid={`devis-tab-${t.value}`}
              className={`px-3 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition ${
                active ? `border-[#52a675] ${t.color}` : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {t.label}
              <span className={`ml-1.5 inline-block text-[10px] px-1.5 py-0.5 rounded-full ${
                active ? "bg-[#52a675]/10 text-[#52a675]" : "bg-gray-100 text-gray-500"
              }`}>{count}</span>
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
            <Input
              placeholder="Rechercher (n°, client, titre)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
              data-testid="devis-search"
            />
          </div>
        </div>
        <Button onClick={() => navigate("/facturation/devis/nouveau")} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="devis-create-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouveau devis
        </Button>
      </div>

      {loading && <div className="text-center py-10 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}

      {!loading && items.length === 0 && (
        <Card className="p-10 text-center text-gray-400 border-dashed border-gray-300">
          Aucun devis. Cliquez sur "Nouveau devis" pour démarrer.
        </Card>
      )}

      {!loading && items.length > 0 && (
        <div className="space-y-2">
          {items.map((d) => {
            const cfg = STATUT_CONFIG[d.statut] || STATUT_CONFIG.en_suspens;
            const Icon = cfg.icon;
            const isOR = d.source === "or";
            return (
              <Card
                key={`${d.source}-${d.id}`}
                className="p-4 border-gray-200 hover:border-[#52a675] hover:shadow-md cursor-pointer transition"
                onClick={() => handleRowClick(d)}
                data-testid={`devis-row-${d.id}`}
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-mono font-semibold text-sm text-gray-900">{d.numero}</span>
                      <Badge variant="outline" className={`${cfg.color} text-[10px]`}>
                        <Icon className="w-3 h-3 mr-1" /> {cfg.label}
                      </Badge>
                      {isOR ? (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[10px]" title="Devis émis depuis un OR atelier">
                          <Wrench className="w-3 h-3 mr-1" /> Atelier · OR {d.or_numero}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200 text-[10px]" title="Devis créé directement depuis Facturation">
                          <FileText className="w-3 h-3 mr-1" /> Direct
                        </Badge>
                      )}
                      {d.facture_id && (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
                          <Receipt className="w-3 h-3 mr-1" /> Facturé
                        </Badge>
                      )}
                      {d.cycle === "expire" && (
                        <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 text-[10px]" title="Date de validité dépassée">
                          <Clock className="w-3 h-3 mr-1" /> Expiré
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm font-semibold text-gray-800">{d.contact_label}</div>
                    <div className="text-xs text-gray-500 truncate max-w-md">
                      {d.titre}
                      {d.vehicule_label && <span className="text-gray-400"> · {d.vehicule_label}</span>}
                    </div>
                    <div className="text-[11px] text-gray-400 mt-0.5">
                      {fmtDate(d.date_emission)}
                      {d.date_validite && <> · validité jusqu'au {fmtDate(d.date_validite)}</>}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">
                      {d.montant_ttc != null ? fmtChf(d.montant_ttc) : "—"}{" "}
                      <span className="text-xs text-gray-500 font-normal">CHF TTC</span>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <DevisDetail devisId={detail?.id} onClose={() => setDetail(null)} onChange={fetch} />
    </div>
  );
}

// ============================================================
// Detail (modal — actions selon statut)
// ============================================================
function DevisDetail({ devisId, onClose, onChange }) {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lienPublic, setLienPublic] = useState("");
  const [showPlanifier, setShowPlanifier] = useState(false);
  const [showConvertirOr, setShowConvertirOr] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showImportExcel, setShowImportExcel] = useState(false);
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    if (!devisId) return;
    setLoading(true);
    api.get(`/devis/${devisId}`).then(({ data }) => {
      setD(data);
      const base = process.env.REACT_APP_BACKEND_URL || "";
      if (data.token_public) setLienPublic(`${base}/devis/${data.token_public}`);
      else setLienPublic("");
    }).finally(() => setLoading(false));
  }, [devisId]);

  const envoyer = async () => {
    try {
      const { data } = await api.post(`/devis/${devisId}/envoyer`);
      setLienPublic(data.lien_public);
      toast.success("Lien public généré");
      const { data: d2 } = await api.get(`/devis/${devisId}`);
      setD(d2);
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const envoyerParSMS = async () => {
    if (!window.confirm("Envoyer le devis par SMS au client (avec lien public) ?")) return;
    try {
      const { data } = await api.post(`/devis/${devisId}/envoyer`, { send_sms: true });
      setLienPublic(data.lien_public);
      if (data.sms?.ok === false) {
        toast.error(`SMS non envoyé : ${data.sms.error || "erreur"}`);
      } else {
        toast.success("SMS envoyé au client avec le lien du devis");
      }
      const { data: d2 } = await api.get(`/devis/${devisId}`);
      setD(d2);
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  // iter318 — Envoi WhatsApp : le CRM gère seul le consentement puis le lien.
  const [whatsappEnvoi, setWhatsappEnvoi] = useState(false);
  const envoyerParWhatsApp = async () => {
    if (!window.confirm("Envoyer le devis au client par WhatsApp ?")) return;
    setWhatsappEnvoi(true);
    try {
      const { data } = await api.post(`/devis/${devisId}/envoyer`, { send_whatsapp: true });
      setLienPublic(data.lien_public);
      const w = data.whatsapp || {};
      if (!w.envoye) {
        toast.error(w.motif || "Envoi WhatsApp impossible");
      } else if (w.etape === "consentement_demande") {
        toast.success("Accord WhatsApp demandé au client — le devis partira dès qu'il accepte");
      } else {
        toast.success("Devis envoyé par WhatsApp");
      }
      const { data: d2 } = await api.get(`/devis/${devisId}`);
      setD(d2);
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setWhatsappEnvoi(false); }
  };

  // iter200 — Envoi par email : ouvre le modal type Boîte mail
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const openEmailModal = () => setEmailModalOpen(true);

  // iter132.i — Copie le lien public sans rien envoyer (cas WhatsApp manuel).
  const copierLienPublic = async () => {
    try {
      const { data } = await api.get(`/devis/${devisId}/public-link`);
      setLienPublic(data.lien_public);
      try {
        await navigator.clipboard.writeText(data.lien_public);
        toast.success("Lien copié — colle-le dans WhatsApp 👍");
      } catch {
        // Fallback navigateurs anciens / contexte non sécurisé
        toast.success(`Lien : ${data.lien_public}`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const changerStatut = async (statut) => {
    if (!window.confirm(`Marquer ce devis comme ${STATUT_CONFIG[statut].label} ?`)) return;
    try {
      await api.patch(`/devis/${devisId}`, { statut });
      toast.success("Statut mis à jour");
      const { data } = await api.get(`/devis/${devisId}`);
      setD(data);
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const supprimer = async () => {
    if (!window.confirm("Supprimer définitivement ce devis ?")) return;
    try {
      await api.delete(`/devis/${devisId}`);
      toast.success("Devis supprimé");
      onClose();
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const convertirEnFacture = async () => {
    if (!window.confirm(`Convertir ce devis en facture native ?\n\nLes lignes, rabais et véhicule seront copiés.\nLes écritures comptables seront générées automatiquement.`)) return;
    setConverting(true);
    try {
      const { data } = await api.post(`/devis/${devisId}/convertir`, { cible: "facture" });
      toast.success(`Facture ${data.numero} créée`);
      onChange();
      navigate(`/facturation/${data.facture_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la conversion");
    } finally { setConverting(false); }
  };

  const convertirEnOR = async (typeClientTarif) => {
    setConverting(true);
    try {
      const { data } = await api.post(`/devis/${devisId}/convertir`, { cible: "or", type_client_tarif: typeClientTarif });
      toast.success(`OR ${data.numero} créé`);
      setShowConvertirOr(false);
      onChange();
      navigate(`/atelier/or/${data.or_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la conversion");
    } finally { setConverting(false); }
  };

  const ouvrirPDF = async () => {
    try {
      const r = await api.get(`/devis/${devisId}/pdf`, { responseType: "blob" });
      const objUrl = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `devis-${d?.numero || devisId}.pdf`;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objUrl), 10000);
    } catch (e) {
      toast.error("Impossible d'ouvrir le PDF");
    }
  };

  const copyLien = () => {
    navigator.clipboard.writeText(lienPublic);
    toast.success("Lien copié");
  };

  if (!devisId) return null;
  const cfg = d && (STATUT_CONFIG[d.statut] || STATUT_CONFIG.en_suspens);
  const Icon = cfg?.icon;

  return (
    <>
      <Dialog open={!!devisId} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Devis {d?.numero}
              {cfg && <Badge variant="outline" className={`${cfg.color} text-xs`}><Icon className="w-3 h-3 mr-1" /> {cfg.label}</Badge>}
              {d?.cycle === "expire" && (
                <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 text-xs" title="Date de validité dépassée">
                  <Clock className="w-3 h-3 mr-1" /> Expiré
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          {loading || !d ? (
            <div className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin mx-auto text-gray-400" /></div>
          ) : (
            <div className="space-y-4 py-2">
              {/* Récap */}
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 text-sm space-y-1">
                <div><strong>Client :</strong> {d.contact_label}</div>
                {d.vehicule_label && <div><strong>Véhicule :</strong> {d.vehicule_label}</div>}
                <div><strong>Titre :</strong> {d.titre || "—"}</div>
                <div className="text-xs text-gray-500">Émis le {fmtDate(d.date_emission)} · valide jusqu'au {fmtDate(d.date_validite)}</div>
              </div>

              {/* iter70 — Édition inline du véhicule (plaque, châssis, marque, modèle, 1ère MEC) */}
              {d.vehicule_detail && (
                <VehiculeCard
                  vehicule={d.vehicule_detail}
                  onUpdate={async () => {
                    const { data } = await api.get(`/devis/${devisId}`);
                    setD(data);
                  }}
                />
              )}

              {/* Lignes */}
              <div>
                <Label className="text-xs uppercase tracking-wider mb-2 block">Lignes</Label>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 uppercase">Libellé</th>
                        <th className="px-2 py-1.5 text-right text-[10px] font-semibold text-gray-500 uppercase">Qté</th>
                        <th className="px-2 py-1.5 text-right text-[10px] font-semibold text-gray-500 uppercase">PU</th>
                        <th className="px-2 py-1.5 text-right text-[10px] font-semibold text-gray-500 uppercase">TVA</th>
                        <th className="px-2 py-1.5 text-right text-[10px] font-semibold text-gray-500 uppercase">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.lignes.map((l, i) => (
                        <tr key={i} className="border-t border-gray-100">
                          <td className="px-2 py-1.5">{l.libelle}</td>
                          <td className="px-2 py-1.5 text-right font-mono">{l.quantite} {l.unite}</td>
                          <td className="px-2 py-1.5 text-right font-mono">{fmtChf(l.prix_unitaire_ht)}</td>
                          <td className="px-2 py-1.5 text-right text-xs text-gray-500">{l.tva_taux}%</td>
                          <td className="px-2 py-1.5 text-right font-mono">{fmtChf((l.quantite || 0) * (l.prix_unitaire_ht || 0))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Totaux */}
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 space-y-1">
                <div className="flex justify-between text-sm"><span>Total HT</span><span className="font-mono">{fmtChf(d.montant_ht)} CHF</span></div>
                <div className="flex justify-between text-sm"><span>TVA</span><span className="font-mono">{fmtChf(d.tva)} CHF</span></div>
                <div className="flex justify-between text-base font-bold pt-1 border-t border-gray-300"><span>Total TTC</span><span className="font-mono text-[#52a675]">{fmtChf(d.montant_ttc)} CHF</span></div>
              </div>

              {/* Lien public si envoyé */}
              {lienPublic && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-testid="devis-lien-public">
                  <div className="text-xs font-semibold text-blue-900 mb-2 flex items-center gap-1">
                    <Link2 className="w-3.5 h-3.5" /> Lien public
                  </div>
                  <div className="flex items-center gap-2">
                    <Input value={lienPublic} readOnly className="text-xs bg-white" />
                    <Button size="sm" variant="outline" onClick={copyLien}><Copy className="w-3.5 h-3.5" /></Button>
                  </div>
                  {d.envoye_at && <div className="text-[10px] text-blue-700 mt-1">Envoyé le {fmtDate(d.envoye_at)}</div>}
                </div>
              )}

              {/* Actions selon statut */}
              <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-200">
                <Button variant="outline" size="sm" onClick={ouvrirPDF} data-testid="devis-pdf-btn"><Eye className="w-3.5 h-3.5 mr-1" /> PDF</Button>
                {d.statut === "en_suspens" && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => setShowEdit(true)} data-testid="devis-edit-btn">
                      <Pencil className="w-3.5 h-3.5 mr-1" /> Modifier
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setShowImportExcel(true)} data-testid="devis-import-excel-btn">
                      <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> Importer panier
                    </Button>
                    <Button variant="outline" size="sm" onClick={envoyer} data-testid="devis-envoyer-btn">
                      <Send className="w-3.5 h-3.5 mr-1" /> {lienPublic ? "Régénérer lien" : "Générer lien public"}
                    </Button>
                    <Button size="sm" variant="outline" className="border-purple-300 text-purple-700 hover:bg-purple-50" onClick={copierLienPublic} data-testid="devis-copier-lien-btn" title="Copier le lien dans le presse-papier (pour coller dans WhatsApp)">
                      📋 Copier le lien
                    </Button>
                    <Button size="sm" variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50" onClick={envoyerParSMS} data-testid="devis-sms-btn">
                      <Send className="w-3.5 h-3.5 mr-1" /> Envoyer par SMS
                    </Button>
                    <Button size="sm" variant="outline" className="border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10" onClick={envoyerParWhatsApp} disabled={whatsappEnvoi} data-testid="devis-whatsapp-btn" title="Le CRM demande l'accord du client si nécessaire, puis envoie le lien">
                      <MessageCircle className="w-3.5 h-3.5 mr-1" /> {whatsappEnvoi ? "Envoi…" : "Envoyer par WhatsApp"}
                    </Button>
                    <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={openEmailModal} data-testid="devis-email-btn">
                      <Send className="w-3.5 h-3.5 mr-1" /> Envoyer par email
                    </Button>
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => changerStatut("accepte")} data-testid="devis-accepter-btn">
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Marquer accepté
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => changerStatut("refuse")} data-testid="devis-refuser-btn">
                      <XCircle className="w-3.5 h-3.5 mr-1" /> Marquer refusé
                    </Button>
                  </>
                )}
                {d.statut === "accepte" && !d.facture_id && !d.or_id && (
                  <>
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => setShowPlanifier(true)} data-testid="devis-planifier-btn">
                      <Calendar className="w-3.5 h-3.5 mr-1" /> Planifier au calendrier
                    </Button>
                    <Button size="sm" className="bg-[#52a675] hover:bg-[#3d8a5c]" onClick={convertirEnFacture} disabled={converting} data-testid="devis-convertir-facture-btn">
                      {converting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Receipt className="w-3.5 h-3.5 mr-1" />}
                      Convertir en facture
                    </Button>
                    <Button size="sm" variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50" onClick={() => setShowConvertirOr(true)} disabled={converting || !d.vehicule_id} title={!d.vehicule_id ? "Véhicule requis pour OR" : ""} data-testid="devis-convertir-or-btn">
                      <Wrench className="w-3.5 h-3.5 mr-1" /> Convertir en OR atelier
                    </Button>
                  </>
                )}
                {d.statut === "accepte" && d.facture_id && (
                  <div className="flex items-center gap-2 text-xs text-emerald-700 italic bg-emerald-50 px-2 py-1.5 rounded">
                    <Receipt className="w-3.5 h-3.5" />
                    ✓ Converti en facture {d.facture_numero}
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => navigate(`/facturation/${d.facture_id}`)} data-testid="devis-goto-facture">
                      <ArrowRight className="w-3 h-3" />
                    </Button>
                  </div>
                )}
                {d.statut === "accepte" && d.or_id && (
                  <div className="flex items-center gap-2 text-xs text-orange-700 italic bg-orange-50 px-2 py-1.5 rounded">
                    <Wrench className="w-3.5 h-3.5" />
                    ✓ Converti en OR {d.or_numero}
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => navigate(`/atelier/or/${d.or_id}`)} data-testid="devis-goto-or">
                      <ArrowRight className="w-3 h-3" />
                    </Button>
                  </div>
                )}
                {d.statut === "planifie" && d.rdv_id && (
                  <div className="text-xs text-blue-700 italic">✓ RDV créé à l'agenda · ID {d.rdv_id.slice(0, 8)}…</div>
                )}
                {d.statut === "en_suspens" && (
                  <Button size="sm" variant="outline" className="text-red-600 border-red-200 ml-auto" onClick={supprimer} data-testid="devis-delete-btn">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <PlanifierDialog
        open={showPlanifier}
        devis={d}
        onClose={() => setShowPlanifier(false)}
        onDone={async () => {
          setShowPlanifier(false);
          const { data } = await api.get(`/devis/${devisId}`);
          setD(data);
          onChange();
        }}
      />

      {/* Dialog : édition du devis (mode édition de DevisForm) */}
      <DevisForm
        open={showEdit}
        editDevis={d}
        onClose={() => setShowEdit(false)}
        onCreated={async () => {
          setShowEdit(false);
          const { data } = await api.get(`/devis/${devisId}`);
          setD(data);
          onChange();
        }}
      />

      {/* Dialog : import Excel (panier fournisseur) — composant mutualisé avec OR */}
      <ExcelImportDialog
        open={showImportExcel}
        onClose={() => setShowImportExcel(false)}
        entityType="devis"
        entityId={devisId}
        onImported={async () => {
          const { data } = await api.get(`/devis/${devisId}`);
          setD(data);
          onChange();
        }}
      />

      {/* Dialog : choix type tarif pour OR */}
      <Dialog open={showConvertirOr} onOpenChange={(o) => !o && setShowConvertirOr(false)}>
        <DialogContent className="max-w-md" data-testid="devis-convertir-or-dialog">
          <DialogHeader>
            <DialogTitle>Conversion en OR Atelier</DialogTitle>
            <DialogDescription>
              Sélectionnez le type de tarif client. Les lignes, rabais et véhicule du devis seront copiés sur l'OR.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-3">
            {[
              { v: "particulier", lib: "Particulier (tarif standard)" },
              { v: "assurance", lib: "Assurance (avec accord préalable)" },
              { v: "flotte", lib: "Flotte / Entreprise" },
              { v: "interne", lib: "Interne (revente, réseau)" },
            ].map((opt) => (
              <Button
                key={opt.v}
                onClick={() => convertirEnOR(opt.v)}
                disabled={converting}
                variant="outline"
                className="w-full justify-start h-auto py-2.5"
                data-testid={`devis-convertir-or-${opt.v}`}
              >
                <Wrench className="w-4 h-4 mr-2 text-orange-600" />
                <span className="text-sm">{opt.lib}</span>
              </Button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertirOr(false)}>Annuler</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* iter200 — Modal d'envoi email métier (Facture / Devis / OR) */}
      <EmailComposeModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        kind="devis"
        entityId={devisId}
        entityLabel={d?.numero ? `Devis ${d.numero}` : "Devis"}
        onSent={async () => {
          const { data: d2 } = await api.get(`/devis/${devisId}`);
          setD(d2);
          onChange?.();
        }}
      />
    </>
  );
}

function PlanifierDialog({ open, devis, onClose, onDone }) {
  const [date, setDate] = useState("");
  const [creneau, setCreneau] = useState("matin");
  const [types, setTypes] = useState([]);
  const [typeId, setTypeId] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // iter362 — SMS de confirmation et véhicule de prêt, comme sur un OR
  const [sms, setSms] = useState(false);
  const [pret, setPret] = useState({ enabled: false, flotte_id: "", duree_jours: 1 });
  const [dispo, setDispo] = useState([]);

  // iter71 — Total MO heures du devis (affiché pour info)
  const heuresMO = (devis?.lignes || [])
    .filter((l) => (l.type_ligne || "") === "main_oeuvre")
    .reduce((sum, l) => sum + (parseFloat(l.quantite) || 0), 0);

  useEffect(() => {
    if (!open) return;
    setDate(""); setCreneau("matin"); setTypeId(""); setNotes("");
    setSms(false); setPret({ enabled: false, flotte_id: "", duree_jours: 1 }); setDispo([]);
    api.get("/types-intervention").then(({ data }) => setTypes(data || []));
  }, [open]);

  const periodePret = () => {
    const start = `${date}T${creneau === "matin" ? "08:00" : "13:30"}:00`;
    const end = new Date(new Date(start).getTime() + (pret.duree_jours || 1) * 86400000);
    return { date_debut: start, date_fin: end.toISOString().slice(0, 19) };
  };

  useEffect(() => {
    if (!open || !pret.enabled || !date) { setDispo([]); return; }
    api.get("/reservations/flotte-disponible", { params: periodePret() })
      .then(({ data }) => {
        const list = data || [];
        setDispo(list);
        setPret((p) => (p.flotte_id && !list.some((f) => f.id === p.flotte_id) ? { ...p, flotte_id: "" } : p));
      })
      .catch(() => setDispo([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pret.enabled, pret.duree_jours, date, creneau]);

  const submit = async () => {
    if (!date) { toast.error("Sélectionnez une date"); return; }
    if (pret.enabled && !pret.flotte_id) { toast.error("Choisissez un véhicule de prêt"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/devis/${devis.id}/planifier`, {
        date, creneau, type_intervention_id: typeId || null, notes, sms,
      });
      toast.success("Devis planifié au calendrier");
      if (sms) {
        if (data?.sms?.envoye) toast.success("Client prévenu par SMS");
        else toast.warning(`SMS non envoyé : ${data?.sms?.detail || "indisponible"}`);
      }
      if (pret.enabled && pret.flotte_id) {
        const p = periodePret();
        try {
          await api.post("/reservations", {
            flotte_id: pret.flotte_id,
            rdv_id: data?.rdv_id || null,
            client_id: devis.client_id,
            date_debut: p.date_debut,
            date_fin: p.date_fin,
            commentaire: `Devis ${devis.numero}`,
          });
          toast.success("Véhicule de prêt réservé");
        } catch (e) {
          toast.warning("Devis planifié, mais la réservation du véhicule a échoué : "
            + (e.response?.data?.detail || "erreur"));
        }
      }
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Planifier au calendrier</DialogTitle>
          <DialogDescription>Crée un rendez-vous lié à ce devis.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Date *</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="planif-date" />
            </div>
            <div>
              <Label className="text-xs">Créneau</Label>
              <Select value={creneau} onValueChange={setCreneau}>
                <SelectTrigger data-testid="planif-creneau"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="matin">Matin</SelectItem>
                  <SelectItem value="apres_midi">Après-midi</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Type d'intervention</Label>
            <Select value={typeId} onValueChange={(v) => setTypeId(v === "_none" ? "" : v)}>
              <SelectTrigger data-testid="planif-type"><SelectValue placeholder="Aucun" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">Aucun (intervention générique)</SelectItem>
                {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Optionnel" data-testid="planif-notes" />
          </div>
          {heuresMO > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-2.5 text-xs text-blue-900" data-testid="planif-mo-info">
              <strong>🕒 Temps MO du devis :</strong> {heuresMO.toFixed(2)} h
              <div className="text-blue-700 mt-0.5">
                Le RDV sera créé avec cette durée. Les lignes du devis seront reprises automatiquement dans l'OR.
              </div>
            </div>
          )}

          {/* iter362 — Mêmes options que la planification d'un OR */}
          <div className="border-t border-gray-100 pt-3 space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
              <input
                type="checkbox" checked={sms} onChange={(e) => setSms(e.target.checked)}
                className="w-4 h-4 accent-[#52a675]" data-testid="planif-sms"
              />
              Prévenir le client par SMS de cette date
            </label>
            <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
              <input
                type="checkbox" checked={pret.enabled}
                onChange={(e) => setPret((p) => ({ ...p, enabled: e.target.checked }))}
                className="w-4 h-4 accent-[#52a675]" data-testid="planif-pret"
              />
              Véhicule de prêt
            </label>
            {pret.enabled && (
              <div className="grid grid-cols-2 gap-2 pl-6" data-testid="planif-pret-options">
                <div>
                  <Label className="text-xs">Durée du prêt (jours)</Label>
                  <Input
                    type="number" min="1" value={pret.duree_jours}
                    onChange={(e) => setPret((p) => ({ ...p, duree_jours: Math.max(1, parseInt(e.target.value, 10) || 1) }))}
                    data-testid="planif-pret-duree"
                  />
                </div>
                <div>
                  <Label className="text-xs">Véhicule disponible</Label>
                  <Select value={pret.flotte_id} onValueChange={(v) => setPret((p) => ({ ...p, flotte_id: v }))}>
                    <SelectTrigger data-testid="planif-pret-vehicule">
                      <SelectValue placeholder={dispo.length ? "Choisir…" : "Aucun disponible"} />
                    </SelectTrigger>
                    <SelectContent>
                      {dispo.map((f) => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.plaque} · {f.marque} {f.modele}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={submitting || !date} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="planif-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calendar className="w-4 h-4 mr-2" />}
            Planifier
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
